
import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './EnterIconMerit.css';

const EnterIconMerit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bankBalance, setBankBalance] = useState(0);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [entries, setEntries] = useState([]);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [formData, setFormData] = useState({
    dogId: '',
    class: '',
    primaryArtUrl: '',
    shortStory: '',
    extraImages: ['', '', ''],
    staffId: '',
    usedItemId: ''
  });
  const [wordCount, setWordCount] = useState(0); // New state for word count
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const backendUrl = `${API_BASE_URL}`;

  useEffect(() => {
    const playerId = localStorage.getItem('playerId');
    console.log('Checking localStorage:', { playerId });
    if (!playerId) {
      console.error('No playerId found');
      setSubmitError('Please log in to enter the show');
      navigate('/login');
      return;
    }

    async function fetchData() {
      try {
        setLoading(true);
        console.log('Fetching show:', `${backendUrl}/merit-confo/shows/${id}`);
        const showResponse = await fetch(`${backendUrl}/merit-confo/shows/${id}`, {
          headers: { 'player-id': playerId }
        });
        const showData = await showResponse.json();
        if (!showResponse.ok) throw new Error(`Show fetch failed: ${showData.error || showResponse.statusText} (Status: ${showResponse.status})`);
        console.log('Show fetched:', showData);
        setShow(showData);

        console.log('Fetching dogs:', `${backendUrl}/iconmerit/dogs`);
        const dogsResponse = await fetch(`${backendUrl}/iconmerit/dogs`, {
          headers: { 'player-id': playerId }
        });
        const dogsData = await dogsResponse.json();
        if (!dogsResponse.ok) throw new Error(`Dogs fetch failed: ${dogsData.error || dogsResponse.statusText} (Status: ${dogsResponse.status})`);
        console.log('Dogs fetched:', dogsData);
        setDogs(dogsData);

        console.log('Fetching staff:', `${backendUrl}/iconmerit/staff`);
        const staffResponse = await fetch(`${backendUrl}/iconmerit/staff`, {
          headers: { 'player-id': playerId }
        });
        const staffData = await staffResponse.json();
        if (!staffResponse.ok) throw new Error(`Staff fetch failed: ${staffData.error || showResponse.statusText} (Status: ${staffResponse.status})`);
        console.log('Staff fetched:', staffData);
        setStaff(staffData);

        console.log('Fetching bank:', `${backendUrl}/bank/${playerId}`);
        const bankResponse = await fetch(`${backendUrl}/bank/${playerId}`, {
          headers: { 'player-id': playerId }
        });
        const bankData = await bankResponse.json();
        if (!bankResponse.ok) throw new Error(`Bank fetch failed: ${bankData.error || showResponse.statusText} (Status: ${bankResponse.status})`);
        console.log('Bank fetched:', bankData);
        setBankBalance(bankData.dfgDollars);

        console.log('Fetching inventory:', `${backendUrl}/iconmerit/inventory/${playerId}`);
        const inventoryResponse = await fetch(`${backendUrl}/iconmerit/inventory/${playerId}`, {
          headers: { 'player-id': playerId }
        });
        const inventoryData = await inventoryResponse.json();
        if (!inventoryResponse.ok) throw new Error(`Inventory fetch failed: ${inventoryData.error || showResponse.statusText} (Status: ${inventoryResponse.status})`);
        console.log('Inventory fetched:', inventoryData);
        setInventoryItems(inventoryData);

        console.log('Fetching entries:', `${backendUrl}/iconmerit/entries?showId=${id}&playerId=${playerId}`);
        const entriesResponse = await fetch(`${backendUrl}/iconmerit/entries?showId=${id}&playerId=${playerId}`, {
          headers: { 'player-id': playerId }
        });
        const entriesData = await entriesResponse.json();
        console.log('Raw entries response:', entriesData);
        if (!entriesResponse.ok) throw new Error(`Entries fetch failed: ${entriesData.error || showResponse.statusText} (Status: ${entriesResponse.status})`);
        const fetchedEntries = Array.isArray(entriesData.entries) ? entriesData.entries : Array.isArray(entriesData) ? entriesData : [];
        console.log('Entries fetched:', fetchedEntries);
        setEntries(fetchedEntries);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err.message, err.stack);
        setSubmitError(`Failed to load form: ${err.message}`);
        setLoading(false);
      }
    }
    fetchData();
  }, [navigate, id]);

  const isShowOpen = () => {
    if (!show) return false;
    const now = new Date();
    return now >= new Date(show.startDate) && now <= new Date(show.endDate);
  };

  const getShowStatus = () => {
    if (!show) return 'Loading...';
    const now = new Date();
    if (now < new Date(show.startDate)) return 'This show is not open yet';
    if (now > new Date(show.endDate)) return 'This show has closed';
    return 'Open';
  };

  const availableDogs = dogs
    .filter(
      (dog) =>
        (!entries.some((entry) => entry.dogId?._id === dog._id) ||
          (editingEntryId && entries.find((e) => e._id === editingEntryId)?.dogId?._id === dog._id))
    )
    .sort((a, b) => a.registeredName.localeCompare(b.registeredName));

  const getEntryFee = () => {
    if (!show || !formData.staffId) return show?.entryFee || 500;
    const selectedStaff = staff.find(s => s._id === formData.staffId);
    if (!selectedStaff) return show.entryFee;
    const canineEvalSkill = selectedStaff.skills.find(skill => skill.name === 'Canine Evaluation');
    if (!canineEvalSkill) return show.entryFee;
    if (canineEvalSkill.level >= 4) return 0; // Free for Certified Judge (4) and Renowned Judge (5)
    if (canineEvalSkill.level === 3) return show.entryFee * 0.5; // 50% for Professional Handler (3)
    return show.entryFee;
  };

  const validateForm = () => {
    const newErrors = {};
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
    if (!formData.dogId) newErrors.dogId = 'Please select a dog';
    if (!formData.class) newErrors.class = 'Please select a class';
    if (!formData.primaryArtUrl) {
      newErrors.primaryArtUrl = 'Primary art URL is required';
    } else if (!urlRegex.test(formData.primaryArtUrl.trim()) || !formData.primaryArtUrl.includes('deviantart.com') || formData.primaryArtUrl.includes('sta.sh')) {
      newErrors.primaryArtUrl = 'Must be a valid DeviantArt URL (e.g., https://www.deviantart.com/username/art/123, not sta.sh)';
    }
    if (formData.extraImages.some(url => url && (!urlRegex.test(url.trim()) || !url.includes('deviantart.com') || url.includes('sta.sh')))) {
      newErrors.extraImages = 'Extra images must be valid DeviantArt URLs (not sta.sh)';
    }
    if (formData.shortStory && wordCount > 500) {
      newErrors.shortStory = 'Short story exceeds 500 words';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'shortStory' ? value : value.trim()
    }));
    if (name === 'shortStory') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
    }
  };

  const handleExtraImageChange = (index, value) => {
    const newExtraImages = [...formData.extraImages];
    newExtraImages[index] = value.trim();
    setFormData(prev => ({ ...prev, extraImages: newExtraImages }));
  };

  const handleEdit = (entry) => {
    setEditingEntryId(entry._id);
    const shortStory = entry.shortStory || '';
    setFormData({
      dogId: entry.dogId?._id || '',
      class: entry.class,
      primaryArtUrl: entry.primaryArtUrl,
      shortStory: shortStory,
      extraImages: entry.extraImages.length ? entry.extraImages.concat(Array(3 - entry.extraImages.length).fill('')) : ['', '', ''],
      staffId: entry.staffId || '',
      usedItemId: entry.usedItem?.itemId || ''
    });
    const words = shortStory.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSuccessMessage('');
    console.log('Form data on submit:', formData);

    if (!isShowOpen()) {
      setSubmitError('Show is not open for entries');
      return;
    }

    if (!validateForm()) {
      console.log('Validation errors:', errors);
      return;
    }

    try {
      const playerId = localStorage.getItem('playerId');
      const body = {
        showId: id,
        dogId: formData.dogId,
        class: formData.class,
        primaryArtUrl: formData.primaryArtUrl,
        shortStory: formData.shortStory,
        extraImages: formData.extraImages.filter(url => url),
        staffId: formData.staffId || null,
        usedItemId: formData.usedItemId || null
      };
      const url = editingEntryId ? `${backendUrl}/iconmerit/entries/${editingEntryId}` : `${backendUrl}/iconmerit/entries`;
      const method = editingEntryId ? 'PATCH' : 'POST';
      console.log('Submitting to:', url, { method, body });
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      console.log('Submission response:', { status: response.status, data });
      if (!response.ok) {
        throw new Error(`Submission failed: ${data.error || response.statusText} (Status: ${response.status})`);
      }
      console.log('Entry submitted successfully:', data);
      setSuccessMessage(editingEntryId ? 'Entry updated successfully!' : 'Entry submitted successfully!');
      setEditingEntryId(null);
      setFormData({
        dogId: '',
        class: '',
        primaryArtUrl: '',
        shortStory: '',
        extraImages: ['', '', ''],
        staffId: '',
        usedItemId: ''
      });
      setWordCount(0); // Reset word count on submit
      const entriesResponse = await fetch(`${backendUrl}/iconmerit/entries?showId=${id}&playerId=${playerId}`, {
        headers: { 'player-id': playerId }
      });
      const entriesData = await entriesResponse.json();
      console.log('Raw entries response after submit:', entriesData);
      const fetchedEntries = Array.isArray(entriesData.entries) ? entriesData.entries : Array.isArray(entriesData) ? entriesData : [];
      console.log('Entries fetched after submit:', fetchedEntries);
      setEntries(fetchedEntries);
      setTimeout(() => navigate(`/merit-confo/show/${id}`), 2000);
    } catch (err) {
      console.error('Submission error:', err.message, err.stack);
      setSubmitError(`Failed to submit entry: ${err.message}`);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      const playerId = localStorage.getItem('playerId');
      console.log('Deleting entry:', `${backendUrl}/iconmerit/entries/${entryId}`);
      const response = await fetch(`${backendUrl}/iconmerit/entries/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        }
      });
      const data = await response.json();
      console.log('Deletion response:', { status: response.status, data });
      if (!response.ok) {
        throw new Error(`Deletion failed: ${data.error || response.statusText} (Status: ${response.status})`);
      }
      console.log('Entry deleted successfully:', data);
      setSuccessMessage('Entry deleted successfully!');
      setEntries(entries.filter(entry => entry._id !== entryId));
      if (editingEntryId === entryId) {
        setEditingEntryId(null);
        setFormData({
          dogId: '',
          class: '',
          primaryArtUrl: '',
          shortStory: '',
          extraImages: ['', '', ''],
          staffId: '',
          usedItemId: ''
        });
        setWordCount(0); // Reset word count on delete if editing
      }
    } catch (err) {
      console.error('Deletion error:', err.message, err.stack);
      setSubmitError(`Failed to delete entry: ${err.message}`);
    }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (submitError) return <div className="error-message">{submitError}</div>;
  if (!show) return <div className="error-message">Show not found</div>;

  return (
    <div className="enter-icon-merit-container">
      <h2>{editingEntryId ? 'Edit Entry for' : 'Enter'} {show.name}</h2>
      {getShowStatus() !== 'Open' && (
        <div className="show-status-banner">
          {getShowStatus()}
        </div>
      )}
      {successMessage && <div className="success-message">{successMessage}</div>}
      <form onSubmit={handleSubmit} className="entry-form">
        <div className="form-group">
          <label htmlFor="dogId">Select Dog:</label>
          <select
            id="dogId"
            name="dogId"
            value={formData.dogId}
            onChange={handleInputChange}
            className={errors.dogId ? 'error' : ''}
          >
            <option value="">Select a dog</option>
            {availableDogs.map(dog => (
              <option key={dog._id} value={dog._id}>
                {dog.registeredName} ({dog.breed})
              </option>
            ))}
          </select>
          {errors.dogId && <span className="error-text">{errors.dogId}</span>}
          {availableDogs.length === 0 && (
            <span className="error-text">No eligible purebred dogs available.</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="class">Class:</label>
          <select
            id="class"
            name="class"
            value={formData.class}
            onChange={handleInputChange}
            className={errors.class ? 'error' : ''}
          >
            <option value="">Select a class</option>
            <option value="Open">Open</option>
            <option value="Bred-By">Bred-By</option>
            <option value="Champion">Champion</option>
          </select>
          {errors.class && <span className="error-text">{errors.class}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="staffId">Select Staff:</label>
          <select
            id="staffId"
            name="staffId"
            value={formData.staffId}
            onChange={handleInputChange}
          >
            <option value="">No staff</option>
            {staff.map(s => (
              <option key={s._id} value={s._id}>
                {s.staffName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="primaryArtUrl">Primary Art URL (DeviantArt):</label>
          <input
            type="text"
            id="primaryArtUrl"
            name="primaryArtUrl"
            value={formData.primaryArtUrl}
            onChange={handleInputChange}
            className={errors.primaryArtUrl ? 'error' : ''}
            placeholder="https://www.deviantart.com/username/art/123456789"
          />
          <p className="help-text">Enter a DeviantArt URL (e.g., https://www.deviantart.com/username/art/123). Sta.sh links are not allowed.</p>
          {formData.primaryArtUrl && (
            <a href={formData.primaryArtUrl} target="_blank" rel="noopener noreferrer" className="art-link">
              View Primary Art
            </a>
          )}
          {errors.primaryArtUrl && <span className="error-text">{errors.primaryArtUrl}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="shortStory">Short Story (Optional, max 500 words):</label>
          <textarea
            id="shortStory"
            name="shortStory"
            value={formData.shortStory}
            onChange={handleInputChange}
            rows="4"
            placeholder="Tell a story about your dog's preparation..."
            className={errors.shortStory ? 'error' : ''}
          />
          <p className={`word-count ${wordCount > 500 ? 'word-count-error' : wordCount > 450 ? 'word-count-warning' : ''}`}>
            Words: {wordCount}/500
          </p>
          {errors.shortStory && <span className="error-text">{errors.shortStory}</span>}
        </div>

        <div className="form-group">
          <label>Extra Images (Optional, DeviantArt, max 3):</label>
          {formData.extraImages.map((url, index) => (
            <div key={index} className="extra-image-group">
              <input
                type="text"
                value={url}
                onChange={(e) => handleExtraImageChange(index, e.target.value)}
                placeholder={`Extra image URL ${index + 1}`}
                className={errors.extraImages ? 'error' : 'extra-image-input'}
              />
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer" className="art-link">
                  View Image {index + 1}
                </a>
              )}
            </div>
          ))}
          {errors.extraImages && <span className="error-text">{errors.extraImages}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="usedItemId">Use Item (Optional):</label>
          <select
            id="usedItemId"
            name="usedItemId"
            value={formData.usedItemId}
            onChange={handleInputChange}
          >
            <option value="">No item</option>
            {inventoryItems.map(item => (
              <option key={item.itemId} value={item.itemId}>
                {item.name} (Qty: {item.quantity})
              </option>
            ))}
          </select>
          {formData.usedItemId && inventoryItems.find(item => item.itemId === formData.usedItemId)?.artUrl && (
            <img
              src={inventoryItems.find(item => item.itemId === formData.usedItemId).artUrl}
              alt="Selected item"
              className="item-preview"
            />
          )}
        </div>

        <div className="form-group">
          <p><strong>Entry Fee:</strong> {getEntryFee()} DFG$</p>
          <p><strong>Your Balance:</strong> {bankBalance} DFG$</p>
          <p><strong>Show Status:</strong> {getShowStatus()}</p>
        </div>

        <div className="button-group">
          <button type="submit" className="submit-button" disabled={!isShowOpen()}>
            {editingEntryId ? 'Update Entry' : 'Enter Show'}
          </button>
          {editingEntryId && (
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setEditingEntryId(null);
                setFormData({
                  dogId: '',
                  class: '',
                  primaryArtUrl: '',
                  shortStory: '',
                  extraImages: ['', '', ''],
                  staffId: '',
                  usedItemId: ''
                });
                setWordCount(0); // Reset word count on cancel edit
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {entries.length > 0 && (
        <div className="entries-card">
          <h3>Your Entries for {show.name}</h3>
          {entries.map(entry => (
            <div key={entry._id} className="entry-item">
              <p><strong>Dog:</strong> <Link to={`/dog/${entry.dogId?._id}`} className="dog-link">{entry.dogId?.registeredName || 'Unknown'}</Link> ({entry.dogId?.breed || 'Unknown'})</p>
              <p><strong>Owner:</strong> {entry.playerId?.username || 'Unknown'}</p>
              <p><strong>Staff:</strong> {entry.staffName || 'None'}</p>
              <p><strong>Class:</strong> {entry.class}</p>
              <p><strong>Primary Art:</strong> <a href={entry.primaryArtUrl} target="_blank" rel="noopener noreferrer" className="art-link">View Art</a></p>
              <p><strong>Entry:</strong> <a href={entry.primaryArtUrl} target="_blank" rel="noopener noreferrer" className="art-link">View Entry</a></p>
              {entry.shortStory && <p><strong>Story:</strong> {entry.shortStory.substring(0, 100)}...</p>}
              {entry.extraImages.length > 0 && (
                <p><strong>Extra Images:</strong> {entry.extraImages.map((url, index) => (
                  <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="art-link">Image {index + 1}</a>
                ))}</p>
              )}
              <p><strong>Item:</strong> {entry.usedItem ? `${entry.usedItem.name} (+${entry.usedItem.showBonus} points)` : 'None'}</p>
              {isShowOpen() && (
                <div className="entry-actions">
                  <button className="edit-button" onClick={() => handleEdit(entry)}>Edit</button>
                  <button className="delete-button" onClick={() => handleDelete(entry._id)}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnterIconMerit;